<template>
  <div class="project-appeal">
    <!-- 筛选卡片 -->
    <div class="filter-card">
      <div class="filter-field">
        <label class="filter-label">年份</label>
        <el-select v-model="selectedYear" placeholder="全部年份" clearable @change="onYearMonthChange" class="filter-select" style="width: 120px;">
          <el-option v-for="year in yearOptions" :key="year" :label="year + '年'" :value="year"></el-option>
        </el-select>
      </div>
      <div class="filter-field">
        <label class="filter-label">月份</label>
        <el-select v-model="selectedMonth" placeholder="全部月份" clearable @change="onYearMonthChange" class="filter-select" style="width: 120px;">
          <el-option v-for="month in monthOptions" :key="month" :label="month + '月'" :value="month"></el-option>
        </el-select>
      </div>
      <div class="filter-field">
        <label class="filter-label">申诉期</label>
        <el-select v-model="selectedBatchId" placeholder="选择申诉批次" clearable @change="loadRecords" class="filter-select">
          <el-option 
            v-for="batch in filteredBatches" 
            :key="batch.id" 
            :label="`${batch.batchName || batch.batchNo} (${batch.answerCount}份)`" 
            :value="batch.id"
          ></el-option>
        </el-select>
      </div>
      <div class="filter-field filter-field--search">
        <label class="filter-label">搜索</label>
        <el-input 
          v-model="searchKeyword" 
          placeholder="搜索经销商代码 / 名称" 
          prefix-icon="el-icon-search"
          clearable
          class="filter-input"
          @keyup.enter.native="loadRecords"
        ></el-input>
      </div>
      <div class="filter-field filter-field--btn">
        <el-button type="primary" icon="el-icon-search" class="filter-search-btn" @click="loadRecords">搜索</el-button>
      </div>
    </div>

    <!-- 批次时间信息 + 操作按钮 -->
    <div class="batch-info" v-if="selectedBatch">
      <div class="batch-info-left">
        <span class="batch-time">申诉开始时间：<b>{{ formatTime(selectedBatch.startTime) }}</b></span>
        <span class="batch-time">申诉截止时间：<b>{{ formatTime(selectedBatch.endTime) }}</b></span>
        <el-tag :type="getAppealTimeStatus.type" size="small" effect="plain" class="batch-status-tag">{{ getAppealTimeStatus.text }}</el-tag>
        <el-button type="text" icon="el-icon-edit" class="batch-edit-btn" @click="showEditTimeDialog">修改时间</el-button>
      </div>
      <div class="batch-info-right">
        <el-button size="small" icon="el-icon-download" @click="exportBatchReviewExcel">导出审核</el-button>
        <el-upload
          :action="'/api/Appeal/batch/importReview/' + (selectedBatchId || '')"
          :headers="{ Authorization: 'Bearer ' + (token || '') }"
          :show-file-list="false"
          accept=".xlsx"
          :on-success="onImportSuccess"
          :on-error="onImportError"
          style="display: inline-block; margin-left: 8px;"
        >
          <el-button size="small" icon="el-icon-upload2">导入审核</el-button>
        </el-upload>
      </div>
    </div>

    <!-- 修改时间对话框 -->
    <el-dialog
      title="修改申诉时间"
      :visible.sync="editTimeDialogVisible"
      width="480px"
      custom-class="edit-time-dialog"
      :close-on-click-modal="false"
    >
      <div class="edit-time-form">
        <div class="edit-time-row edit-time-row--divider">
          <label class="edit-time-label">申诉开始时间</label>
          <el-date-picker
            v-model="editTimeForm.startTime"
            type="datetime"
            placeholder="选择开始时间"
            class="edit-time-picker"
          ></el-date-picker>
        </div>
        <div class="edit-time-row">
          <label class="edit-time-label">申诉结束时间</label>
          <el-date-picker
            v-model="editTimeForm.endTime"
            type="datetime"
            placeholder="选择结束时间"
            class="edit-time-picker"
          ></el-date-picker>
        </div>
      </div>
      <div slot="footer" class="edit-time-footer">
        <el-button @click="editTimeDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="editTimeSubmitting" @click="submitEditTime">确定</el-button>
      </div>
    </el-dialog>

    <!-- 申诉记录列表 -->
    <el-table :data="records" v-loading="loading" border style="width: 100%" class="appeal-table">
      <el-table-column type="index" label="序号" width="70" align="center"></el-table-column>
      <el-table-column prop="dealerCode" label="经销商代码" min-width="180">
        <template slot-scope="{ row }">{{ row.dealerCode || '-' }}</template>
      </el-table-column>
      <el-table-column prop="dealerName" label="经销商名称" min-width="180">
        <template slot-scope="{ row }">{{ row.dealerName || '-' }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="getStatusType(row.status)" size="small" effect="plain">
            {{ getStatusText(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="提交时间" min-width="160">
        <template slot-scope="{ row }">
          {{ formatTime(row.submitTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right" align="center">
        <template slot-scope="{ row }">
          <!-- <el-button type="text" class="op-link" @click="viewDetail(row)">查看详情</el-button> -->
          <el-button type="text" class="op-link op-link--primary" @click="goToReviewPage(row)" v-if="row.status === 1 || row.status === 2">审核</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadRecords"
      ></el-pagination>
    </div>

    <!-- 申诉详情对话框（只读查看） -->
    <el-dialog title="申诉详情" :visible.sync="detailDialogVisible" width="900px">
      <div v-if="currentRecord" class="appeal-detail readonly-mode">
        <div class="detail-header">
          <div class="info-item">
            <label>经销商：</label>
            <span>{{ currentRecord.dealerName }} ({{ currentRecord.dealerCode }})</span>
          </div>
          <div class="info-item">
            <label>状态：</label>
            <el-tag :type="getStatusType(currentRecord.status)" size="small">
              {{ getStatusText(currentRecord.status) }}
            </el-tag>
          </div>
        </div>
        
        <el-divider content-position="left">申诉题目</el-divider>
        
        <!-- 题目列表（只读） -->
        <div class="appeal-items">
          <div v-for="item in currentItems" :key="item.id" class="appeal-item">
            <div class="item-header">
              <span class="item-label" v-html="item.itemLabel"></span>
              <el-tag :type="getReviewStatusType(item.reviewStatus)" size="mini">
                {{ getReviewStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="item-content">
              <div class="remark-section">
                <label>申诉备注：</label>
                <p>{{ item.appealRemark || '无' }}</p>
              </div>
              
              <div class="evidence-section" v-if="item.evidenceUrls">
                <label>证据材料：</label>
                <div class="evidence-list" v-viewer="{movable: false}">
                  <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                    <div v-if="isImage(file)" :key="idx" class="evidence-item image">
                      <img :src="file.url" />
                      <span class="file-name">{{ file.name || '图片' }}</span>
                    </div>
                    <div v-else :key="idx" class="evidence-item file">
                      <a :href="file.url" target="_blank">
                        <i class="el-icon-document"></i>
                        {{ file.name || '附件' }}
                      </a>
                    </div>
                  </template>
                </div>
              </div>
              
              <!-- 已审核状态显示 -->
              <div class="review-result" v-if="item.reviewStatus !== 0">
                <div class="result-remark" v-if="item.reviewRemark">
                  <label>审核备注：</label>
                  <span>{{ item.reviewRemark }}</span>
                </div>
                <div class="result-score" v-if="item.reviewStatus === 1">
                  <label>还分：</label>
                  <span class="score">+{{ item.restoredScore }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    
    <!-- 申诉审核对话框（带审核功能） -->
    <el-dialog 
      title="申诉审核" 
      :visible.sync="reviewDialogVisible" 
      width="1000px"
      :close-on-click-modal="false"
    >
      <div v-if="currentRecord" class="appeal-review">
        <div class="review-header">
          <div class="info-group">
            <span class="dealer-info">{{ currentRecord.dealerName }} ({{ currentRecord.dealerCode }})</span>
            <el-tag :type="getStatusType(currentRecord.status)" size="small">
              {{ getStatusText(currentRecord.status) }}
            </el-tag>
          </div>
        </div>
        
        <!-- 题目列表（可审核） -->
        <div class="appeal-items-review">
          <div v-for="item in currentItems" :key="item.id" class="appeal-item-card">
            <div class="card-header">
              <span class="item-label">{{ item.itemLabel }}</span>
              <el-tag :type="getReviewStatusType(item.reviewStatus)" size="mini">
                {{ getReviewStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="card-body">
              <div class="appeal-content">
                <div class="field">
                  <label>申诉说明：</label>
                  <div class="value">{{ item.appealRemark || '无' }}</div>
                </div>
                
                <div class="field" v-if="item.evidenceUrls">
                  <label>凭证材料：</label>
                  <div class="evidence-gallery" v-viewer="{movable: false}">
                    <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                      <div v-if="isImage(file)" :key="idx" class="evidence-thumb">
                        <img :src="file.url" />
                      </div>
                      <a v-else :key="idx" :href="file.url" target="_blank" class="evidence-file">
                        <i class="el-icon-document"></i>
                        {{ file.name || '附件' }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- 审核操作区 -->
              <div class="review-action-area" v-if="item.reviewStatus === 0">
                <div class="action-row">
                  <el-input 
                    v-model="item.reviewRemarkInput" 
                    placeholder="审核备注（可选）"
                    size="small"
                    style="flex: 1; max-width: 400px;"
                  ></el-input>
                  <el-input-number 
                    v-model="item.restoredScoreInput" 
                    :min="0" 
                    :max="item.originalScore"
                    placeholder="还分" 
                    size="small"
                    style="width: 120px;"
                    controls-position="right"
                  ></el-input-number>
                </div>
                <div class="action-buttons">
                  <el-button type="success" size="small" icon="el-icon-check" @click="reviewItem(item, 1)">通过</el-button>
                  <el-button type="danger" size="small" icon="el-icon-close" @click="reviewItem(item, 2)">不通过</el-button>
                </div>
              </div>
              
              <!-- 已审核结果 -->
              <div class="reviewed-result" v-else>
                <el-alert 
                  :type="item.reviewStatus === 1 ? 'success' : 'error'" 
                  :closable="false"
                  show-icon
                >
                  <template slot="title">
                    {{ item.reviewStatus === 1 ? '已通过' : '已驳回' }}
                    <span v-if="item.restoredScore" class="restored-score">（还分 +{{ item.restoredScore }}）</span>
                  </template>
                  <span v-if="item.reviewRemark">{{ item.reviewRemark }}</span>
                </el-alert>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="reviewDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="finishReview" :disabled="hasUnreviewed">完成审核</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'index/axios'

export default {
  name: 'ProjectAppeal',

  data() {
    return {
      token: localStorage.getItem('ACCESS_TOKEN') || '',
      loading: false,
      batches: [],
      selectedBatchId: null,
      searchKeyword: '',
      records: [],
      page: 1,
      pageSize: 20,
      total: 0,
      
      // 年月筛选
      selectedYear: '',
      selectedMonth: '',
      yearOptions: [],
      monthOptions: [],
      // 详情（只读）
      detailDialogVisible: false,
      // 审核
      reviewDialogVisible: false,
      currentRecord: null,
      currentItems: [],
      // 编辑时间
      editTimeDialogVisible: false,
      editTimeSubmitting: false,
      editTimeForm: {
        startTime: '',
        endTime: ''
      }
    }
  },

  computed: {
    projectId() {
      return this.$route.params.projectId
    },
    // 当前选中的批次
    selectedBatch() {
      return this.batches.find(b => b.id === this.selectedBatchId) || null
    },
    // 根据年月过滤后的批次列表
    filteredBatches() {
      return this.batches.filter(b => {
        if (!b.appealMonth) return true
        const [y, m] = b.appealMonth.split('-')
        const matchYear = !this.selectedYear || y === String(this.selectedYear)
        // 注意月份可能有前导零
        const matchMonth = !this.selectedMonth || parseInt(m, 10) === parseInt(this.selectedMonth, 10)
        return matchYear && matchMonth
      })
    },
    // 申诉时间状态
    getAppealTimeStatus() {
      if (!this.selectedBatch) return { type: 'info', text: '-' }
      
      const now = new Date()
      const start = this.selectedBatch.startTime ? new Date(this.selectedBatch.startTime) : null
      const end = this.selectedBatch.endTime ? new Date(this.selectedBatch.endTime) : null
      
      if (!start || !end) return { type: 'info', text: '时间未设置' }
      
      if (now < start) {
        return { type: 'warning', text: '未开始' }
      } else if (now > end) {
        return { type: 'danger', text: '已结束' }
      } else {
        return { type: 'success', text: '进行中' }
      }
    },
    // 是否有未审核的题目
    hasUnreviewed() {
      return this.currentItems.some(item => item.reviewStatus === 0)
    }
  },

  mounted() {
    this.loadBatches()
  },

  methods: {
    async loadBatches() {
      try {
        const res = await axios.post('/Appeal/batch/list', {
          projectId: Number(this.projectId),
          page: 1,
          pageSize: 100
        })
        
        // 尝试多种响应格式
        let records = res?.data?.data?.records   // { data: { data: { records } } }
                   || res?.data?.records         // { data: { records } }
                   || res?.records               // { records }
                   || []
        
        this.batches = records
        this.extractYearMonthOptions()
        
        if (this.batches.length > 0) {
          const defaultBatch = this.filteredBatches.length > 0 ? this.filteredBatches[0] : this.batches[0]
          this.selectedBatchId = defaultBatch.id
          this.loadRecords()
        }
      } catch (e) {
        console.error('加载批次失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.records) {
          this.batches = data.records
          if (this.batches.length > 0) {
            const defaultBatch = this.filteredBatches.length > 0 ? this.filteredBatches[0] : this.batches[0]
            this.selectedBatchId = defaultBatch.id
            this.loadRecords()
          }
        }
      }
    },

    extractYearMonthOptions() {
      const years = new Set()
      const months = new Set()
      this.batches.forEach(b => {
        if (b.appealMonth) {
          const [y, m] = b.appealMonth.split('-')
          if (y) years.add(y)
          if (m) months.add(parseInt(m, 10).toString()) // 转成无前导零的字符串
        }
      })
      this.yearOptions = Array.from(years).sort((a, b) => b - a)
      this.monthOptions = Array.from(months).sort((a, b) => a - b)
    },

    onYearMonthChange() {
      // 检查当前选中的批次是否还在过滤后的列表中
      const isStillValid = this.filteredBatches.some(b => b.id === this.selectedBatchId)
      if (!isStillValid) {
        this.selectedBatchId = this.filteredBatches.length > 0 ? this.filteredBatches[0].id : null
        this.loadRecords()
      }
    },

    async loadRecords() {
      if (!this.selectedBatchId) {
        this.records = []
        return
      }
      this.loading = true
      try {
        const res = await axios.post('/Appeal/record/list', {
          batchId: this.selectedBatchId,
          dealerCode: this.searchKeyword,
          page: this.page,
          pageSize: this.pageSize
        })
        
        // 解析响应: res.data.data.records
        let records = res?.data?.data?.records
                   || res?.data?.records
                   || []
        let total = res?.data?.data?.total
                 || res?.data?.total
                 || 0
        
        this.records = records
        this.total = total
      } catch (e) {
        console.error('加载申诉记录失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.records) {
          this.records = data.records
          this.total = data.total || 0
        }
      } finally {
        this.loading = false
      }
    },

    async viewDetail(record) {
      try {
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        
        // 处理多层嵌套: res.data.data 或 res.data
        const data = res?.data?.data || res?.data || res
        
        this.currentRecord = data.record || record
        
        // 过滤：只保留已填写申诉说明或有证据材料的指标
        const rawItems = data.items || []
        const filteredItems = rawItems.filter(item => 
          item.appealRemark || (item.evidenceUrls && item.evidenceUrls !== '[]')
        )
        
        this.currentItems = filteredItems.map(item => ({
          ...item,
          reviewRemarkInput: '',
          restoredScoreInput: item.originalScore || 0
        }))
        
        this.detailDialogVisible = true
      } catch (e) {
        console.error('加载详情失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.items) {
          this.currentRecord = data.record || record
          
          const rawItems = data.items || []
          const filteredItems = rawItems.filter(item => 
            item.appealRemark || (item.evidenceUrls && item.evidenceUrls !== '[]')
          )
          
          this.currentItems = filteredItems.map(item => ({
            ...item,
            reviewRemarkInput: '',
            restoredScoreInput: item.originalScore || 0
          }))
          this.detailDialogVisible = true
        } else {
          this.$message.error('加载详情失败')
        }
      }
    },

    async processAppeal(record) {
      // 加载详情并打开审核弹窗
      try {
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        
        const data = res?.data?.data || res?.data || res
        
        this.currentRecord = data.record || record
        
        // 过滤：只保留已填写申诉说明或有证据材料的指标
        const rawItems = data.items || []
        const filteredItems = rawItems.filter(item => 
          item.appealRemark || (item.evidenceUrls && item.evidenceUrls !== '[]')
        )
        
        this.currentItems = filteredItems.map(item => ({
          ...item,
          reviewRemarkInput: '',
          restoredScoreInput: item.originalScore || 0
        }))
        
        this.reviewDialogVisible = true
      } catch (e) {
        console.error('加载详情失败', e)
        const data = e?.data?.data || e?.data
        if (data?.items) {
          this.currentRecord = data.record || record
          
          // 过滤：只保留已填写申诉说明或有证据材料的指标
          const rawItems = data.items || []
          const filteredItems = rawItems.filter(item => 
            item.appealRemark || (item.evidenceUrls && item.evidenceUrls !== '[]')
          )
          
          this.currentItems = filteredItems.map(item => ({
            ...item,
            reviewRemarkInput: '',
            restoredScoreInput: item.originalScore || 0
          }))
          this.reviewDialogVisible = true
        } else {
          this.$message.error('加载详情失败')
        }
      }
    },
    
    async finishReview() {
      const unreviewedCount = this.currentItems.filter(item => item.reviewStatus === 0).length
      if (unreviewedCount > 0) {
        this.$message.warning(`还有 ${unreviewedCount} 个题目未审核`)
        return
      }
      if (!this.currentRecord || !this.currentRecord.id) {
        this.reviewDialogVisible = false
        return
      }
      
      try {
        await axios.post('/Appeal/record/complete', {
          recordId: this.currentRecord.id
        })
        this.currentRecord.status = 3
        const recordIndex = this.records.findIndex(record => record.id === this.currentRecord.id)
        if (recordIndex > -1) {
          this.$set(this.records, recordIndex, {
            ...this.records[recordIndex],
            status: 3
          })
        }
        this.$message.success('审核完成')
        this.reviewDialogVisible = false
        this.loadRecords()
      } catch (e) {
        console.error('Complete review failed', e)
        this.$message.error('完成审核失败')
      }
    },

    async reviewItem(item, status) {
      try {
        const res = await axios.post('/Appeal/item/review', {
          itemId: item.id,
          reviewStatus: status,
          reviewRemark: item.reviewRemarkInput,
          restoredScore: status === 1 ? item.restoredScoreInput : 0
        })
        const data = res?.data?.data || res?.data || res || {}
        this.$message.success(status === 1 ? '审核通过，已还分' : '审核不通过')
        
        item.reviewStatus = status
        item.reviewRemark = item.reviewRemarkInput
        if (status === 1) {
          item.restoredScore = item.restoredScoreInput
        }

        const nextRecordStatus = Number(data.recordStatus || 0)
        if (nextRecordStatus && this.currentRecord) {
          this.currentRecord.status = nextRecordStatus
          const recordIndex = this.records.findIndex(record => record.id === this.currentRecord.id)
          if (recordIndex > -1) {
            this.$set(this.records, recordIndex, {
              ...this.records[recordIndex],
              status: nextRecordStatus
            })
          }
        }
      } catch (e) {
        console.error('Review item failed', e)
        this.$message.error('审核失败')
      }
    },

    getStatusType(status) {
      const map = { 0: 'info', 1: 'warning', 2: 'primary', 3: 'success' }
      return map[status] || 'info'
    },

    getStatusText(status) {
      const map = { 0: '待申诉', 1: '已提交', 2: '审核中', 3: '已完成' }
      return map[status] || '未知'
    },

    getReviewStatusType(status) {
      const map = { 0: 'info', 1: 'success', 2: 'danger' }
      return map[status] || 'info'
    },

    getReviewStatusText(status) {
      const map = { 0: '待审核', 1: '已通过', 2: '不通过' }
      return map[status] || '未知'
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      return new Date(timeStr).toLocaleString('zh-CN')
    },

    parseEvidenceUrls(urlsStr) {
      if (!urlsStr) return []
      try {
        const parsed = JSON.parse(urlsStr)
        // 新格式: [{name, url}, ...]
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            // 如果是对象，提取 url
            if (typeof item === 'object' && item.url) {
              return item
            }
            // 如果是字符串，转换为对象
            return { url: item, name: '' }
          })
        }
        return []
      } catch {
        // 旧格式: 逗号分隔的 URL
        return urlsStr.split(',').filter(u => u).map(url => ({ url, name: '' }))
      }
    },

    isImage(urlOrFile) {
      const url = typeof urlOrFile === 'object' ? urlOrFile.url : urlOrFile
      if (!url) return false
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || url.includes('/image/')
    },

    previewImage(url) {
      window.open(url, '_blank')
    },

    // 跳转到独立审核页面
    goToReviewPage(record) {
      if (!this.selectedBatchId) {
        this.$message.warning('请先选择申诉批次')
        return
      }
      const targetRecord = record || this.currentRecord
      this.$router.push({
        path: '/appeal-review',
        query: {
          batchId: this.selectedBatchId,
          recordId: targetRecord && targetRecord.id ? targetRecord.id : ''
        }
      })
    },

    // 导出当前批次全部申诉审核Excel
    async exportBatchReviewExcel() {
      if (!this.selectedBatchId) {
        this.$message.warning('请先选择申诉批次')
        return
      }
      try {
        this.$message.info('正在导出，请稍候...')
        const res = await fetch(`/api/Appeal/batch/exportReview/${this.selectedBatchId}`, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + (localStorage.getItem('ACCESS_TOKEN') || '')
          }
        })
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}))
          this.$message.error(errData.message || '导出失败')
          return
        }
        const blob = await res.blob()
        const batchLabel = this.selectedBatch ? (this.selectedBatch.batchName || this.selectedBatch.batchNo || '') : ''
        const fileName = `申诉处理导入_${batchLabel}_${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}.xlsx`
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message.error('导出失败：' + (e.message || ''))
      }
    },
    // 导入审核结果成功回调
    onImportSuccess(res, row) {
      const data = res?.data || res
      if (data?.success) {
        const msg = `导入完成！成功: ${data.successCount || 0}, 跳过: ${data.skipCount || 0}, 错误: ${data.errorCount || 0}`
        if (data.errorCount > 0 && data.errors && data.errors.length > 0) {
          this.$alert(
            `<div>${msg}</div><ul style="max-height:200px;overflow:auto;margin-top:8px;">${data.errors.map(e => `<li>${e}</li>`).join('')}</ul>`,
            '导入结果',
            { dangerouslyUseHTMLString: true, type: 'warning' }
          )
        } else {
          this.$message.success(msg)
        }
        this.loadRecords()
      } else {
        this.$message.error(data?.message || '导入失败')
      }
    },

    // 导入审核结果失败回调
    onImportError(err) {
      console.error('导入失败', err)
      this.$message.error('导入失败，请检查文件格式')
    },

    // 显示编辑时间对话框
    showEditTimeDialog() {
      if (!this.selectedBatch) return
      this.editTimeForm.startTime = this.selectedBatch.startTime ? new Date(this.selectedBatch.startTime) : ''
      this.editTimeForm.endTime = this.selectedBatch.endTime ? new Date(this.selectedBatch.endTime) : ''
      this.editTimeDialogVisible = true
    },

    // 提交编辑时间
    async submitEditTime() {
      if (!this.editTimeForm.startTime || !this.editTimeForm.endTime) {
        this.$message.warning('请选择开始和结束时间')
        return
      }
      
      if (new Date(this.editTimeForm.startTime) >= new Date(this.editTimeForm.endTime)) {
        this.$message.warning('结束时间必须大于开始时间')
        return
      }
      
      this.editTimeSubmitting = true
      try {
        await axios.post('/Appeal/batch/updateTime', {
          batchId: this.selectedBatchId,
          startTime: this.editTimeForm.startTime,
          endTime: this.editTimeForm.endTime
        })
        
        this.$message.success('修改成功')
        this.editTimeDialogVisible = false
        
        // 刷新批次列表
        await this.loadBatches()
      } catch (e) {
        console.error('修改时间失败', e)
        this.$message.error('修改失败')
      } finally {
        this.editTimeSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.project-appeal {
  padding: 0;
}

/* ========== 筛选卡片 ========== */
.filter-card {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 24px 28px;
  margin-bottom: 20px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &--search {
    flex: 1;
  }

  &--btn {
    flex-shrink: 0;
  }
}

.filter-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  line-height: 1;
}

.filter-select {
  width: 240px;
}

.filter-search-btn {
  height: 40px;
  min-width: 100px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
  color: #fff !important;

  &:hover, &:focus {
    background: #2563eb !important;
    border-color: #2563eb !important;
  }
}

/* ========== 批次信息条 ========== */
.batch-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.batch-info-left {
  display: flex;
  align-items: center;
  gap: 28px;
}

.batch-info-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.batch-time {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;

  b {
    font-weight: 500;
    color: #303133;
  }
}

.batch-status-tag {
  flex-shrink: 0;
}

.batch-edit-btn {
  font-size: 14px;
  padding: 0;
}

/* ========== 表格 ========== */
.appeal-table {
  border-radius: 4px;

  ::v-deep  .el-table__header th {
    background: #fafafa;
    color: #909399;
    font-weight: 500;
    font-size: 14px;
    padding: 14px 0;
  }

  ::v-deep  .el-table__body td {
    padding: 16px 0;
    color: #303133;
    font-size: 14px;
  }
}

.op-link {
  font-size: 14px;
  padding: 0;
  color: #3b82f6;

  &--primary {
    color: #3b82f6;
    font-weight: 500;
  }
}

/* ========== 分页 ========== */
.pagination-bar {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* ========== 详情弹窗 ========== */
.appeal-detail {
  .detail-header {
    display: flex;
    gap: 32px;
    margin-bottom: 16px;

    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;

      label {
        color: #909399;
      }
    }
  }
}

.appeal-items {
  .appeal-item {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .item-label {
        font-weight: 500;
      }
    }

    .item-content {
      .remark-section, .evidence-section {
        margin-bottom: 12px;

        label {
          color: #909399;
          font-size: 13px;
          display: block;
          margin-bottom: 4px;
        }

        p {
          margin: 0;
          color: #606266;
        }
      }

      .evidence-list {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .evidence-item {
          img {
            width: 80px;
            height: 80px;
            object-fit: cover;
            border-radius: 4px;
            cursor: pointer;
            border: 1px solid #eee;
          }
        }
      }

      .review-action {
        display: flex;
        gap: 12px;
        align-items: center;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px dashed #eee;
      }

      .review-result {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px dashed #eee;
        display: flex;
        gap: 24px;

        label {
          color: #909399;
        }

        .score {
          color: #67c23a;
          font-weight: 600;
        }
      }
    }
  }
}

/* ========== 审核弹窗 ========== */
.appeal-review {
  .review-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    .info-group {
      display: flex;
      align-items: center;
      gap: 12px;

      .dealer-info {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
    }
  }

  .appeal-items-review {
    max-height: 500px;
    overflow-y: auto;

    .appeal-item-card {
      background: #f9fafc;
      border-radius: 8px;
      margin-bottom: 16px;
      overflow: hidden;

      .card-header {
        padding: 12px 16px;
        background: #f0f2f5;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .item-label {
          font-weight: 500;
          color: #303133;
        }
      }

      .card-body {
        padding: 16px;

        .appeal-content {
          .field {
            margin-bottom: 12px;

            label {
              color: #909399;
              font-size: 13px;
              display: block;
              margin-bottom: 4px;
            }

            .value {
              color: #606266;
            }
          }

          .evidence-gallery {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;

            .evidence-thumb {
              img {
                width: 60px;
                height: 60px;
                object-fit: cover;
                border-radius: 4px;
                cursor: pointer;
                border: 1px solid #e4e7ed;

                &:hover {
                  border-color: #409eff;
                }
              }
            }

            .evidence-file {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 8px;
              background: #f0f2f5;
              border-radius: 4px;
              color: #409eff;
              font-size: 13px;
              text-decoration: none;

              &:hover {
                background: #e6f0ff;
              }
            }
          }
        }

        .review-action-area {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px dashed #e4e7ed;

          .action-row {
            display: flex;
            gap: 12px;
            align-items: center;
            margin-bottom: 12px;
          }

          .action-buttons {
            display: flex;
            gap: 8px;
          }
        }

        .reviewed-result {
          margin-top: 16px;

          .restored-score {
            color: #67c23a;
            font-weight: 500;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
/* 修改时间弹窗 - 非 scoped，因为 el-dialog 渲染在 body 下 */
.edit-time-dialog.el-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;

  .el-dialog__header {
    padding: 28px 32px 20px !important;
    margin-right: 0 !important;
    border-bottom: none !important;
    background: #fff !important;

    .el-dialog__title {
      font-size: 18px !important;
      font-weight: 700 !important;
      color: #1a1a1a !important;
    }

    .el-dialog__headerbtn {
      top: 28px !important;
      right: 28px !important;
      font-size: 20px !important;

      .el-dialog__close {
        color: #c0c4cc !important;
        font-weight: 400 !important;

        &:hover {
          color: #909399 !important;
        }
      }
    }
  }

  .el-dialog__body {
    padding: 8px 32px 32px !important;
    background: #fff !important;
  }

  .el-dialog__footer {
    padding: 20px 32px 28px !important;
    border-top: 1px solid #f0f0f0 !important;
    background: #fff !important;
    text-align: right !important;
  }

  .edit-time-form {
    display: flex;
    flex-direction: column;
  }

  .edit-time-row {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 24px 0;

    &--divider {
      border-bottom: 1px solid #f0f0f0;
    }
  }

  .edit-time-label {
    width: 100px;
    flex-shrink: 0;
    font-size: 15px;
    color: #303133;
    text-align: left;
    white-space: nowrap;
    font-weight: 400;
  }

  .edit-time-picker {
    flex: 1;

    &.el-date-editor {
      width: 100% !important;

      .el-input__inner {
        height: 44px !important;
        line-height: 44px !important;
        border-radius: 8px !important;
        border-color: #e4e7ed !important;
        font-size: 14px !important;
        color: #303133 !important;

        &:focus {
          border-color: #3b82f6 !important;
        }
      }

      .el-input__prefix {
        color: #c0c4cc;
      }
    }
  }

  .edit-time-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .el-button {
      min-width: 80px !important;
      height: 40px !important;
      border-radius: 8px !important;
      font-size: 15px !important;
      font-weight: 500 !important;
      padding: 0 24px !important;
    }

    .el-button--default {
      background: #fff !important;
      border-color: #dcdfe6 !important;
      color: #606266 !important;

      &:hover {
        color: #3b82f6 !important;
        border-color: #3b82f6 !important;
        background: #f0f7ff !important;
      }
    }

    .el-button--primary {
      background: #3b82f6 !important;
      border-color: #3b82f6 !important;
      color: #fff !important;

      &:hover {
        background: #2563eb !important;
        border-color: #2563eb !important;
      }
    }
  }
}
</style>
